import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import * as fromBrandPLP from '../store';
import { Observable } from 'rxjs/internal/Observable';

const minRequestTimeBeforeShowLoading = 500;
const showLoadingTimeInMilliSeconds = 1000;

@Injectable()
export class ProductsLoadingService {
  private wrapperTimer: ReturnType<typeof setTimeout>;
  private loadingTimer: ReturnType<typeof setTimeout>;
  private isProductsLoadingSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private showProductsLoadingSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isProductsLoadedOnceSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public get isProductsLoading$(): Observable<boolean> {
    return this.isProductsLoadingSubject$.asObservable();
  }

  public get showProductsLoading$(): Observable<boolean> {
    return this.showProductsLoadingSubject$.asObservable();
  }

  public get isProductsLoadedOnce$(): Observable<boolean> {
    return this.isProductsLoadedOnceSubject$.asObservable();
  }

  constructor(private store: Store<fromBrandPLP.BrandPLPPageState>) {
    this.store.pipe(select(fromBrandPLP.isProductsLoading))
      .subscribe((isLoading: boolean) => {
        const isLoadedOnce = this.getIsLoadedOnce();
        this.isProductsLoadedOnceSubject$.next(isLoadedOnce(isLoading));
        if (!isLoading) {
          this.showProductsLoadingSubject$.next(false);
          this.clearTimers();
          return;
        }

        this.isProductsLoadingSubject$.next(isLoading);
        this.setWrapperTime();
      });
  }

  // Starts wrapper timer, set loading to true and call next timer after 500ms
  private setWrapperTime(): void {
    this.wrapperTimer = setTimeout(() => {
      this.showProductsLoadingSubject$.next(true);
      this.setLoadingTimer();
    }, minRequestTimeBeforeShowLoading);
  }

  // Starts loading timer, set loading to false after 1000ms
  private setLoadingTimer(): void {
    this.loadingTimer = setTimeout(() => {
      this.showProductsLoadingSubject$.next(false);
      this.clearTimers();
    }, showLoadingTimeInMilliSeconds);
  }

  // Clear all timers
  clearTimers(): void {
    clearTimeout(this.wrapperTimer);
    clearTimeout(this.loadingTimer);
  }

  // First call: assign isLoadedOnce = isLoading and return true
  // Rest of calls: Check for isLoadedOnce === true and isLoading === false for returning null.
  getIsLoadedOnce(): Function {
    let counter = 0;
    let isLoadedOnce = false;
    return function (isLoading: boolean): boolean {
      if (counter < 1) {
        counter++;
        isLoadedOnce = isLoading;
        return true;
      }

      if (isLoadedOnce && !isLoading) {
        isLoadedOnce = null;
        return true;
      }

      return isLoadedOnce;
    };
  }
}
